'use client';

import { useEffect, useMemo, useState } from 'react';

// Style
import { useAuthStore } from '@/app/store/use-auth-store';
import { useBranchStore } from '@/app/store/use-branch-store';
import { useCartStore } from '@/entities/cart/model/use-cart-store';
import productApi from '@/entities/product/api/client';
import { useShipper } from '@/entities/shipper/api/query';
import { NumberToPrice } from '@/features/number-to-price';
import { useRouter } from '@/i18n/navigation';
import AuthWidget from '@/widgets/auth-widget';
import { Button, Center, Flex } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

export default function Bill() {
  const [isAuthDialog, setIsAuthDialog] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  // const [deliveryPrice, setDeliveryPrice] = useState(0);

  const { user } = useAuthStore();
  const { cart } = useCartStore();
  const { branch } = useBranchStore();
  const STOP = useCartStore((state) => state.STOP);
  const EXCLUDE = useCartStore((state) => state.EXCLUDE);
  const EXCLUDE_STOP = useCartStore((state) => state.EXCLUDE_STOP);

  const router = useRouter();
  const t = useTranslations();

  const productIds = useMemo(
    () => String(cart?.map((product) => product.product_id)),
    [cart]
  );
  const productIdQuantity = useMemo(() => {
    const result: { [key: string]: number } = {};

    cart.forEach((product) => {
      result[product?.product_id] = Number(product.quantity);
    });

    return result;
  }, [cart]);

  const onButtonClick = () => {
    if (!user) {
      setIsAuthDialog(true);
    } else {
      router.push(`/checkout`);
    }
  };

  const { data: menuProducts } = useQuery({
    queryKey: ['menu-products', branch?.menu_id, productIds],
    queryFn: () =>
      productApi.getMenuProducts({
        menu_id: branch?.menu_id || '',
        items: JSON.stringify(productIdQuantity),
        partner_shipper_id: branch?.shipper_id,
      }),
    enabled: Boolean(cart?.length > 0 && !!branch?.menu_id),
  });

  const { data: shipper } = useShipper();

  useEffect(() => {
    if (cart.length > 0) {
      let total = 0;
      cart?.map((product) => {
        total += (product.price_with_discount || 0) * product.quantity;
        if (product?.order_modifiers?.length > 0) {
          for (const modifier of product.order_modifiers) {
            total +=
              +modifier.modifier_price *
              +(modifier?.modifier_quantity * product.quantity);
          }
        }
      });
      setTotalPrice(total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  useEffect(() => {
    if (menuProducts) {
      const productsForStop: (string | undefined)[] = [];
      if (cart) {
        for (const product of cart) {
          const menuProduct = menuProducts?.products?.find(
            (item) => item?.productId === product?.product_id
          );
          if (
            !menuProduct?.isActive &&
            !productsForStop.includes(menuProduct?.id)
          ) {
            STOP(product?.product_id);
            productsForStop.push(product?.product_id);
            continue;
          } else if (!menuProduct) {
            STOP(product?.product_id);
            continue;
          } else if (product?.in_stop) {
            EXCLUDE(product?.product_id);
          }
        }
      }
    }
  }, [menuProducts]);

  // Remove product when menu is not found
  useEffect(() => {
    if (!branch?.menu_id) {
      EXCLUDE_STOP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch?.menu_id]);

  const minimalPrice = shipper?.data?.minimal_order_price || 0;

  useEffect(() => {
    router.prefetch('/checkout');
  }, []);

  if (cart.length > 0)
    return (
      <>
        <Center
          position={'fixed'}
          left={0}
          bottom={0}
          w={'full'}
          zIndex={2}
          flexDir="column"
          bg={'background.card'}
          boxShadow={'0px 0px 5px rgba(0, 0, 0, 0.15)'}
        >
          {Boolean(minimalPrice && minimalPrice > totalPrice) && (
            <Flex pt={2} gap={1} color="red" fontSize={14}>
              {t('minimum_order_amount')}
              <NumberToPrice value={minimalPrice} />
            </Flex>
          )}
          <Flex p={4} justify={'space-between'} w={'full'} align={'center'}>
            <NumberToPrice
              // value={totalPrice + deliveryPrice} // delivery never set for now ?
              value={totalPrice}
              textProps={{
                fontWeight: 500,
                fontSize: 20,
              }}
              spanProps={{
                fontWeight: 400,
                fontSize: 20,
              }}
            />
            <Button
              onClick={onButtonClick}
              w={'50%'}
              size={'xl'}
              h={'52px'}
              bg="primary"
              color="primary.text"
              _hover={{ bg: 'primary.hover' }}
              rounded={16}
              disabled={Boolean(minimalPrice && minimalPrice > totalPrice)}
            >
              {t('next')}
            </Button>
          </Flex>
        </Center>
        <AuthWidget
          open={isAuthDialog}
          onClose={() => setIsAuthDialog(false)}
          onLastConfirm={() => router.push('/checkout')}
        />
      </>
    );

  return null;
}
